import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import i18n from '../../../i18n';
import config from '../../../config';
import {
  fetchInitPermissions,
  fetchInitPermissionsSuccess,
  fetchInitPermissionsFailure,
  fetchRoles,
  fetchRolesSuccess,
  fetchRolesFailure,
  fetchRoleById,
  fetchRoleByIdSuccess,
  fetchRoleByIdFailure,
  addRole,
  addRoleSuccess,
  addRoleFailure,
  updateRole,
  updateRoleSuccess,
  updateRoleFailure,
  deleteRole,
  deleteRoleSuccess,
  deleteRoleFailure,
  bulkDeleteRoles,
  bulkDeleteRolesSuccess,
  bulkDeleteRolesFailure,
} from './slice';

/**
 * Translate error from backend
 * Handles both simple error messages and validation errors object
 * @param {string|object} error - Error message string or error object with errors property
 */
function translateError(error) {
  console.log('[translateError] Input error:', error);
  console.log('[translateError] Error type:', typeof error);
  console.log('[translateError] Has errors property:', error?.errors);

  // Handle validation errors object (400 Bad Request with errors property)
  if (error && typeof error === 'object' && error.errors) {
    console.log('[translateError] Processing validation errors');
    // Extract all validation error messages
    const validationErrors = [];
    for (const field in error.errors) {
      const fieldErrors = error.errors[field];
      console.log(`[translateError] Field ${field}:`, fieldErrors);
      if (Array.isArray(fieldErrors)) {
        // Translate validation error messages to Vietnamese
        fieldErrors.forEach(errMsg => {
          console.log('[translateError] Processing error message:', errMsg);
          if (errMsg.includes('must be between')) {
            const translated = i18n.t('roles.errors.nameLength');
            console.log('[translateError] Translated to:', translated);
            validationErrors.push(translated);
          } else if (errMsg.includes('cannot exceed')) {
            const translated = i18n.t('roles.errors.descriptionTooLong');
            console.log('[translateError] Translated to:', translated);
            validationErrors.push(translated);
          } else {
            validationErrors.push(errMsg);
          }
        });
      }
    }
    const result = validationErrors.join('; ');
    console.log('[translateError] Final result:', result);
    return result;
  }

  const errorMessage = error?.message || error;
  console.log('[translateError] Using fallback, errorMessage:', errorMessage);
  if (!errorMessage) return i18n.t('roles.errors.unknown');

  // Common error message mappings
  const errorMap = {
    'Role name already exists': 'roles.errors.nameExists',
    'Role not found': 'roles.errors.roleNotFound',
    'Failed to add role': 'roles.errors.addFailed',
    'Failed to update role': 'roles.errors.updateFailed',
    'Failed to delete role': 'roles.errors.deleteFailed',
    'Failed to fetch roles': 'roles.errors.fetchFailed',
  };

  // Check if error message matches a known pattern
  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  // If no mapping found, return original message
  return errorMessage;
}

/**
 * Transform permissions from checkbox format to API format
 * Input: { role: { addRole: true, deleteRole: false }, user: { addUser: true } }
 * Output: [{ role: { addRole: true, deleteRole: false } }, { user: { addUser: true } }]
 */
function transformPermissionsToAPI(permissions) {
  if (!permissions) return [];

  return Object.keys(permissions).map((category) => ({
    [category]: permissions[category],
  }));
}

// Worker saga: Fetch init permissions
function* handleFetchInitPermissions() {
  try {
    const response = yield call(api.get, config.endpoints.roles.init);
    yield put(fetchInitPermissionsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchInitPermissionsFailure(translatedError));
  }
}

// Worker saga: Fetch roles
function* handleFetchRoles(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, and filterName from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterName } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.name = filterName;
    }

    const response = yield call(api.get, '/roles', params);
    // Pass the whole response (including pagination info)
    yield put(fetchRolesSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchRolesFailure(translatedError));
  }
}

// Worker saga: Fetch role by ID
function* handleFetchRoleById(action) {
  try {
    const roleId = action.payload;
    const response = yield call(api.get, `/roles/${roleId}`);
    yield put(fetchRoleByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchRoleByIdFailure(translatedError));
  }
}

// Worker saga: Add role
function* handleAddRole(action) {
  try {
    // Transform permissions from checkbox format to API format
    const { permissions, ...roleData } = action.payload;
    const transformedPermissions = transformPermissionsToAPI(permissions);
    const dataToSend = { ...roleData, permissions: transformedPermissions };

    const response = yield call(api.post, config.endpoints.roles.create, dataToSend);
    yield put(addRoleSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(addRoleFailure(translatedError));
  }
}

// Worker saga: Update role
function* handleUpdateRole(action) {
  try {
    const { id, permissions, ...roleData } = action.payload;
    // Transform permissions from checkbox format to API format
    const transformedPermissions = transformPermissionsToAPI(permissions);
    const dataToSend = { id, ...roleData, permissions: transformedPermissions };

    const response = yield call(api.put, `${config.endpoints.roles.update}/${id}`, dataToSend);
    yield put(updateRoleSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(updateRoleFailure(translatedError));
  }
}

// Worker saga: Delete role
function* handleDeleteRole(action) {
  try {
    const roleId = action.payload;
    yield call(api.delete, `/roles/${roleId}`);
    yield put(deleteRoleSuccess(roleId));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(deleteRoleFailure(translatedError));
  }
}

// Worker saga: Bulk delete roles
function* handleBulkDeleteRoles(action) {
  try {
    const roleIds = action.payload;
    // Call API DELETE /roles?ids=1&ids=2&ids=3
    // Use custom paramsSerializer to format array as repeated params
    yield call(api.delete, '/roles', {
      params: { ids: roleIds },
      paramsSerializer: {
        indexes: null, // Use repeat format: ?ids=1&ids=2 instead of ?ids[0]=1&ids[1]=2
      },
    });
    yield put(bulkDeleteRolesSuccess(roleIds));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(bulkDeleteRolesFailure(translatedError));
  }
}

// Watcher saga
export function* watchRolesActions() {
  yield takeLatest(fetchInitPermissions.type, handleFetchInitPermissions);
  yield takeLatest(fetchRoles.type, handleFetchRoles);
  yield takeLatest(fetchRoleById.type, handleFetchRoleById);
  yield takeLatest(addRole.type, handleAddRole);
  yield takeLatest(updateRole.type, handleUpdateRole);
  yield takeLatest(deleteRole.type, handleDeleteRole);
  yield takeLatest(bulkDeleteRoles.type, handleBulkDeleteRoles);
}
